import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import Loading from '../components/Loading';
import { useI18n } from '../i18n/index.jsx';

const CONTRACT_TYPES = {
  umowa_o_dzielo: 'Umowa o dzieło (50% KUP)',
  umowa_zlecenia: 'Umowa zlecenie',
};

const POSITIONS_DZIELO = [
  'Content Creator / Twórca treści (UGC)',
  'Video Editor / Montażysta wideo',
  'Motion Designer / Animator',
  'Graphic Designer / Grafik',
  'UI/UX Designer',
  'Web Designer / Projektant stron',
  'Copywriter / Tekściarz',
  'Scenarzysta / Scriptwriter',
  'Fotograf',
  'Ilustrator',
  'Kompozytor / Twórca muzyki',
  'Lektor / Nagranie lektorskie',
  'Tłumacz (przekład dzieła)',
  'Programista / Developer',
  'QA / Testy (raport)',
  'Analityk (raport/analityka)',
  'Specjalista SEO (audyt/raport)',
  'Specjalista Ads (kampania + raport)',
  'Projektant materiałów marketingowych',
  'Autor e-booka / Kursu / Materiałów',
  'Konsultant (strategia/plan)',
  'Architekt rozwiązań (dokumentacja)',
  'Projektant prezentacji / Pitch deck',
];

const POSITIONS_ZLECENIE = [
  'Asystent / Assistant',
  'Obsługa klienta / Customer Support',
  'Specjalista ds. sprzedaży / Sales',
  'Account Manager',
  'Social Media Manager',
  'Community Manager',
  'Moderator',
  'Specjalista ds. administracji',
  'Koordynator / Coordinator',
  'HR / Rekruter',
  'Wirtualna asystentka / VA',
  'Operator danych / Data entry',
  'Specjalista ds. logistyki',
  'Specjalista ds. e-commerce',
  'Manager operacyjny',
  'Pomoc biurowa',
];

export default function CompanySelect() {
  const { user, setPage } = useApp();
  const { t } = useI18n();
  const [companies, setCompanies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ company_id: '', position: '', custom_position: '', contract_type: 'umowa_o_dzielo', message: '' });
  const [useCustomPosition, setUseCustomPosition] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [comp, reqs] = await Promise.all([
        api.get('/companies').catch(() => api.get('/companies/available')).catch(() => ({ data: [] })),
        api.get('/company-requests').catch(() => ({ data: [] })),
      ]);
      setCompanies(comp.data);
      setRequests(reqs.data);
      if (comp.data.length === 1) setForm(f => ({ ...f, company_id: comp.data[0].id }));
    } catch {}
    setLoading(false);
  }

  const positions = form.contract_type === 'umowa_o_dzielo' ? POSITIONS_DZIELO : POSITIONS_ZLECENIE;
  const finalPosition = useCustomPosition ? form.custom_position : form.position;

  // Warn if custom position doesn't match contract type
  const positionWarning = useCustomPosition && form.custom_position && (
    (form.contract_type === 'umowa_o_dzielo' && POSITIONS_ZLECENIE.some(p => form.custom_position.toLowerCase().includes(p.split('/')[0].trim().toLowerCase()))) ||
    (form.contract_type === 'umowa_zlecenia' && POSITIONS_DZIELO.some(p => form.custom_position.toLowerCase().includes(p.split('/')[0].trim().toLowerCase())))
  );

  async function submit() {
    if (!form.company_id) { setMsg('Выберите фирму'); return; }
    if (!finalPosition) { setMsg('Укажите должность'); return; }
    setSending(true); setMsg('');
    try {
      await api.post('/company-requests', {
        company_id: Number(form.company_id),
        position: finalPosition,
        contract_type: form.contract_type,
        message: form.message || undefined,
      });
      setMsg('✅ Заявка отправлена! Ожидайте подтверждения.');
      setShowForm(false);
      load();
    } catch (e) { setMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setSending(false);
  }

  if (loading) return <Loading />;

  const hasPending = requests.some(r => r.status === 'pending');

  return (
    <div className="page">
      <h1>{t('company_select.title')}</h1>

      {requests.length > 0 && (
        <>
          <h3 style={{ margin: '0 0 8px' }}>{t('company_select.my_requests')}</h3>
          <div className="list">
            {requests.map(r => (
              <div key={r.id} className="list-item">
                <div className="list-main">
                  <div className="list-title">{r.company_name || `Фирма #${r.company_id}`}</div>
                  <div className="list-sub">{r.position || '—'} • {CONTRACT_TYPES[r.contract_type] || r.contract_type}</div>
                </div>
                <span className={`status-badge status-${r.status}`}>
                  {r.status === 'pending' ? '⏳' : r.status === 'approved' ? '✅' : '❌'} {r.status}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {showForm ? (
        <div style={{ marginTop: 12 }}>
          <div className="form">
            <div className="field">
              <label>Фирма *</label>
              <select value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })}>
                <option value="">Выберите...</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.short_name || c.name} (NIP: {c.nip})</option>)}
              </select>
            </div>

            <div className="field">
              <label>Тип договора *</label>
              <select value={form.contract_type} onChange={e => {
                setForm({ ...form, contract_type: e.target.value, position: '' });
                setUseCustomPosition(false);
              }}>
                {Object.entries(CONTRACT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>

            <div className="field">
              <label>Должность *</label>
              {!useCustomPosition ? (
                <>
                  <select value={form.position} onChange={e => setForm({ ...form, position: e.target.value })}>
                    <option value="">Выберите должность...</option>
                    {positions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <button className="btn btn-small" onClick={() => setUseCustomPosition(true)} style={{ marginTop: 6 }}>
                    ✏️ Ввести свою должность
                  </button>
                </>
              ) : (
                <>
                  <input value={form.custom_position} onChange={e => setForm({ ...form, custom_position: e.target.value })}
                    placeholder="Введите должность..." />
                  <button className="btn btn-small" onClick={() => setUseCustomPosition(false)} style={{ marginTop: 6 }}>
                    📋 Выбрать из списка
                  </button>
                  {positionWarning && (
                    <div className="banner banner-warning" style={{ marginTop: 6, fontSize: 12 }}>
                      ⚠️ Эта должность может не подходить под {CONTRACT_TYPES[form.contract_type]}. Рекомендуем выбрать другую или согласовать с администратором.
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="field">
              <label>Сообщение</label>
              <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={2} placeholder="Доп. информация..." />
            </div>
          </div>
          {msg && <div className="message" style={{ marginTop: 8 }}>{msg}</div>}
          <div className="actions">
            <button className="btn btn-primary" onClick={submit} disabled={sending || !form.company_id || !finalPosition}>
              {sending ? '⏳...' : '📤 Отправить заявку'}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {hasPending ? (
            <div className="banner banner-info">⏳ Заявка на рассмотрении</div>
          ) : (
            <button className="btn btn-primary" onClick={() => setShowForm(true)} style={{ width: '100%' }}>
              📋 Запросить назначение фирмы
            </button>
          )}
        </div>
      )}

      {!showForm && msg && <div className="message" style={{ marginTop: 8 }}>{msg}</div>}
    </div>
  );
}
